import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  PLATFORM_ID,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

export interface EmojiCategory {
  label: string;
  emojis: string[];
}

export const EMOJI_CATEGORIES: EmojiCategory[] = [
  {
    label: 'Smileys',
    emojis: ['😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌',
             '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭'],
  },
  {
    label: 'Gestures',
    emojis: ['👍', '👎', '👊', '✊', '🤛', '🤜', '🤞', '✌️', '🤟', '🤘', '👌', '🤌', '🤏', '👈',
             '👉', '👆', '👇', '☝️', '👋', '🤚', '🖐', '✋', '🖖', '💪', '🦾', '🤲', '👐', '🙌', '👏'],
  },
  {
    label: 'Hearts',
    emojis: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓',
             '💗', '💖', '💘', '💝', '💟'],
  },
];

@Component({
  selector: 'app-emoji-picker',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './emoji-picker.html',
})
export class EmojiPickerComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  /** When provided, the panel is positioned at these explicit top/left
   *  coordinates instead of the default `absolute` below-left placement. */
  @Input() anchorTop?: number;
  @Input() anchorLeft?: number;
  /** Coordinate space for anchorTop/anchorLeft: 'fixed' (viewport, default —
   *  needed when the trigger sits inside a container that clips overflow) or
   *  'absolute' (relative to the nearest positioned ancestor, so the panel
   *  scrolls along with the page instead of staying pinned to the viewport). */
  @Input() anchorPosition: 'fixed' | 'absolute' = 'fixed';
  /** When provided, the panel is kept inside this element's box instead of the
   *  full window — e.g. a modal's content card, so the panel reads as part of
   *  the dialog it opened from rather than floating over the page behind it.
   *  There is no actual CSS clipping tying the two together, so without this
   *  the panel is free to render past the card into whatever is behind it. */
  @Input() boundsElement?: HTMLElement;
  @Output() pick = new EventEmitter<string>();

  readonly categories = EMOJI_CATEGORIES;
  activeTab = 0;

  @ViewChild('panel') private panelRef?: ElementRef<HTMLElement>;

  /** What the template actually binds to in explicit-anchor mode. Starts as a
   *  copy of anchorTop/anchorLeft, then clampToViewport() nudges it inward. */
  resolvedTop?: number;
  resolvedLeft?: number;

  /**
   * Explicit-anchor mode only. False for the frames between mount and the
   * clamp correction settling, so those frames — rendered at the raw,
   * unclamped anchor — are never actually painted. Without this the panel
   * visibly opens in the wrong spot and only jumps to the right one once
   * something forces a repaint.
   */
  positioned = false;

  /** Bumped on every open/close so a stale retry loop or resize handler from
   *  a previous open can tell it's been superseded and stop touching state. */
  private run = 0;
  private viewportResizeAttached = false;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['isOpen'] || changes['anchorTop'] || changes['anchorLeft']) &&
      this.isOpen && this.anchorTop != null
    ) {
      this.positioned = false;
      this.resolvedTop = this.anchorTop;
      this.resolvedLeft = this.anchorLeft;
      this.clampToViewport();
      this.attachViewportResizeListener();
    } else if (changes['isOpen'] && !this.isOpen) {
      this.run++; // retires any in-flight retry loop
      this.detachViewportResizeListener();
    }
  }

  ngOnDestroy(): void {
    this.run++;
    this.detachViewportResizeListener();
  }

  setTab(i: number): void {
    this.activeTab = i;
  }

  onPick(emoji: string, event: MouseEvent): void {
    event.stopPropagation();
    this.pick.emit(emoji);
  }

  /**
   * On mobile, tapping the trigger button typically blurs whatever text field
   * was focused (the caption box), which collapses the on-screen keyboard —
   * and that collapse resizes the visible viewport over an animation, not
   * instantly. A correction computed mid-animation is stale the moment it
   * lands; nothing besides watching for the next resize would ever notice.
   * `visualViewport` (not `window.resize`, which iOS Safari doesn't fire for
   * the keyboard) reports each step of that animation, so this re-clamps
   * against every one of them until the keyboard finishes moving.
   */
  private attachViewportResizeListener(): void {
    if (!isPlatformBrowser(this.platformId) || this.viewportResizeAttached) return;
    this.viewportResizeAttached = true;
    (window.visualViewport ?? window).addEventListener('resize', this.onViewportResize);
  }

  private detachViewportResizeListener(): void {
    if (!isPlatformBrowser(this.platformId) || !this.viewportResizeAttached) return;
    this.viewportResizeAttached = false;
    (window.visualViewport ?? window).removeEventListener('resize', this.onViewportResize);
  }

  private readonly onViewportResize = (): void => {
    if (this.isOpen && this.anchorTop != null) this.clampToViewport();
  };

  /** How long to keep re-aiming — long enough to outlast a keyboard-collapse
   *  animation on a real device, not just a warm desktop paint. */
  private static readonly CLAMP_SETTLE_MS = 1200;
  /** Frames the target must hold before the loop accepts it as final. */
  private static readonly CLAMP_STABLE_FRAMES = 3;

  /**
   * Every caller anchors the panel to the top-left corner of its trigger
   * button, and several of those buttons sit at the right edge of a narrow
   * container — the caption emoji button in create-post foremost among them —
   * so anchoring blind regularly pushed this w-72 (288px) panel past that
   * edge. Rather than hardcode the panel's size into every caller's math,
   * re-aim against its *actual* rendered box.
   *
   * Bounds default to the window, but a caller inside a modal should pass
   * `boundsElement` (the modal's content card) — the modal box itself doesn't
   * clip its children, so without an explicit bound the panel is free to
   * float past the card into the page behind it even while still comfortably
   * inside the browser window.
   *
   * Runs as a short retry loop rather than a single measurement, because on
   * mobile the viewport itself is often still moving when this first fires —
   * tapping the trigger blurs the caption box, which collapses the on-screen
   * keyboard, and that resize is an animation, not an instant change. A
   * one-shot correction can measure against a viewport that's mid-collapse
   * and be stale the moment it lands; re-measuring each frame (plus the
   * `visualViewport` listener above, for changes after this loop has already
   * exited) closes that gap instead of depending on the user's next touch to
   * paper over it.
   *
   * The correction itself is expressed as a delta (how far the panel
   * overflowed), not a recomputed absolute position — that keeps it correct
   * under both `anchorPosition` modes without needing to know a positioned
   * ancestor's offset, since `getBoundingClientRect()` is viewport-relative
   * either way and a translation is coordinate-system agnostic.
   *
   * Each pass ends with an explicit `detectChanges()` rather than leaving the
   * render to zone.js's own scheduling — mutating a plain property inside a
   * `requestAnimationFrame` callback usually gets picked up automatically,
   * but "usually" left a real gap where the corrected value sat unrendered
   * until some unrelated zone-tracked event forced the overdue repaint.
   */
  private clampToViewport(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const run = ++this.run;
    const deadline = Date.now() + EmojiPickerComponent.CLAMP_SETTLE_MS;
    let stableFrames = 0;

    const step = (): void => {
      // A newer open, a close, or destroy has superseded this loop.
      if (run !== this.run) return;

      const el = this.panelRef?.nativeElement;
      if (!el || this.resolvedLeft == null || this.resolvedTop == null) return;
      let left = this.resolvedLeft;
      let top = this.resolvedTop;

      const margin = 8;
      const rect = el.getBoundingClientRect();
      const bounds = this.boundsElement?.getBoundingClientRect();
      // `visualViewport` tracks the *visible* area — shrunk while the on-screen
      // keyboard is up — which `window.innerWidth/innerHeight` does not.
      const vv = window.visualViewport;
      const viewportWidth = vv?.width ?? window.innerWidth;
      const viewportHeight = vv?.height ?? window.innerHeight;

      // Intersect with the viewport regardless — a bounds element taller/wider
      // than the viewport (a modal on a short screen) must not push the panel
      // off-screen chasing the card's own overflowing edge.
      const minX = Math.max(margin, bounds?.left ?? -Infinity);
      const maxX = Math.min(viewportWidth - margin, bounds?.right ?? Infinity);
      const minY = Math.max(margin, bounds?.top ?? -Infinity);
      const maxY = Math.min(viewportHeight - margin, bounds?.bottom ?? Infinity);

      let dx = 0;
      if (rect.right > maxX) dx = maxX - rect.right;
      if (rect.left + dx < minX) dx = minX - rect.left;

      let dy = 0;
      if (rect.bottom > maxY) dy = maxY - rect.bottom;
      if (rect.top + dy < minY) dy = minY - rect.top;

      if (dx !== 0 || dy !== 0) {
        left += dx;
        top += dy;
        this.resolvedLeft = left;
        this.resolvedTop = top;
        stableFrames = 0;
      } else {
        stableFrames++;
      }

      this.positioned = true;
      this.cdr.detectChanges();

      if (stableFrames < EmojiPickerComponent.CLAMP_STABLE_FRAMES && Date.now() < deadline) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  }
}
